import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  AlertCircle,
  AlertTriangle,
  Info,
  Check,
  X,
  Loader2,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { aiInvoiceApi, timeEntriesApi } from "../../services/api";

// ── Types ───────────────────────────────────────────────────────────────────

interface ReviewIssue {
  entryId: string;
  type: string;
  severity: "error" | "warning" | "suggestion";
  message: string;
  suggestedFix?: string;
  originalText?: string;
}

interface ReviewStats {
  totalEntries: number;
  totalIssues: number;
  errorsCount: number;
  warningsCount: number;
  suggestionsCount: number;
}

interface ReviewResult {
  score: number;
  issues: ReviewIssue[];
  summary: string;
  stats: ReviewStats;
}

interface Props {
  entryIds?: string[];
  matterId?: string;
  dateRange?: { from: string; to: string };
  onClose?: () => void;
  onEntriesUpdated?: () => void;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const typeLabels: Record<string, string> = {
  block_billing: "Block Billing",
  vague: "Vague Narrative",
  duplicate: "Duplicate Entry",
  excessive_time: "Excessive Time",
  missing_info: "Missing Info",
  informal: "Informal Language",
};

const severityConfig = {
  error: {
    icon: AlertCircle,
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    iconColor: "text-red-500",
    badge: "bg-red-100 text-red-700",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    iconColor: "text-amber-500",
    badge: "bg-amber-100 text-amber-700",
  },
  suggestion: {
    icon: Info,
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    iconColor: "text-blue-500",
    badge: "bg-blue-100 text-blue-700",
  },
};

function getScoreColor(score: number) {
  if (score >= 80) return { ring: "text-emerald-500", label: "Excellent", bg: "bg-emerald-50" };
  if (score >= 50) return { ring: "text-amber-500", label: "Needs Attention", bg: "bg-amber-50" };
  return { ring: "text-red-500", label: "Issues Found", bg: "bg-red-50" };
}

// ── Component ───────────────────────────────────────────────────────────────

const AiPreBillReview: React.FC<Props> = ({
  entryIds,
  matterId,
  dateRange,
  onClose,
  onEntriesUpdated,
}) => {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<ReviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [fixingAll, setFixingAll] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [expandedIssue, setExpandedIssue] = useState<number | null>(null);

  // ── Fetch review ────────────────────────────────────────────────────────

  const runReview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDismissedIds(new Set());
    setAppliedIds(new Set());

    try {
      const payload: Record<string, any> = {};
      if (entryIds && entryIds.length > 0) payload.entryIds = entryIds;
      else if (matterId) {
        payload.matterId = matterId;
        if (dateRange) payload.dateRange = dateRange;
      }

      const res = await aiInvoiceApi.reviewEntries(payload);
      setResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to run AI review");
    } finally {
      setLoading(false);
    }
  }, [entryIds, matterId, dateRange]);

  useEffect(() => {
    runReview();
  }, [runReview]);

  // ── Animate score ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!result) return;
    const target = result.score;
    let current = 0;
    const step = Math.max(1, Math.floor(target / 40));
    const interval = setInterval(() => {
      current = Math.min(current + step, target);
      setAnimatedScore(current);
      if (current >= target) clearInterval(interval);
    }, 20);
    return () => clearInterval(interval);
  }, [result]);

  // ── Apply fix ───────────────────────────────────────────────────────────

  const handleApplyFix = async (issue: ReviewIssue) => {
    if (!issue.suggestedFix || !issue.entryId) return;

    setApplyingId(issue.entryId);
    try {
      await timeEntriesApi.update(issue.entryId, {
        narrative: issue.suggestedFix,
      });
      setAppliedIds((prev) => new Set(prev).add(issue.entryId));
      onEntriesUpdated?.();
    } catch (err) {
      console.error("Failed to apply fix:", err);
    } finally {
      setApplyingId(null);
    }
  };

  // ── Fix all ─────────────────────────────────────────────────────────────

  const handleFixAll = async () => {
    if (!result) return;

    const fixableIssues = result.issues.filter(
      (i) =>
        i.suggestedFix &&
        i.entryId &&
        !dismissedIds.has(i.entryId) &&
        !appliedIds.has(i.entryId)
    );

    if (fixableIssues.length === 0) return;

    setFixingAll(true);

    try {
      // Get unique entry IDs that need narrative fixes
      const uniqueEntryIds = Array.from(new Set(fixableIssues.map((i) => i.entryId)));

      const res = await timeEntriesApi.improveBulk(uniqueEntryIds);
      const improved: Record<string, string> = res.data?.improved || {};

      // Apply each improved narrative
      const updatePromises = Object.entries(improved).map(([entryId, narrative]) =>
        timeEntriesApi.update(entryId, { narrative })
      );

      await Promise.all(updatePromises);

      setAppliedIds((prev) => {
        const next = new Set(prev);
        uniqueEntryIds.forEach((id) => next.add(id));
        return next;
      });

      onEntriesUpdated?.();
    } catch (err) {
      console.error("Failed to fix all:", err);
    } finally {
      setFixingAll(false);
    }
  };

  // ── Dismiss ─────────────────────────────────────────────────────────────

  const handleDismiss = (issue: ReviewIssue) => {
    setDismissedIds((prev) => new Set(prev).add(issue.entryId));
  };

  const handleDismissAll = () => {
    if (!result) return;
    const allIds = new Set(result.issues.map((i) => i.entryId));
    setDismissedIds(allIds);
  };

  // ── Filter visible issues ─────────────────────────────────────────────

  const visibleIssues =
    result?.issues.filter(
      (i) => !dismissedIds.has(i.entryId) && !appliedIds.has(i.entryId)
    ) || [];

  // ── Render: Loading ───────────────────────────────────────────────────

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-8"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
            <Sparkles className="w-5 h-5 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            Analyzing Time Entries
          </h3>
          <p className="text-sm text-slate-500 max-w-sm text-center">
            AI is reviewing your entries for billing compliance, vague
            narratives, and potential issues...
          </p>
        </div>
      </motion.div>
    );
  }

  // ── Render: Error ─────────────────────────────────────────────────────

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-red-200 shadow-sm p-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              Review Failed
            </h3>
            <p className="text-sm text-slate-500">{error}</p>
          </div>
          <button
            onClick={runReview}
            className="ml-auto px-4 py-2 text-sm font-medium text-white bg-[#0F5132] hover:bg-[#146C43] rounded-xl transition-colors"
          >
            Retry
          </button>
        </div>
      </motion.div>
    );
  }

  if (!result) return null;

  const scoreColor = getScoreColor(result.score);

  // ── Render: Results ───────────────────────────────────────────────────

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
    >
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">
                AI Billing Review
              </h3>
              <p className="text-sm text-slate-500">
                {result.stats.totalEntries} entries analyzed
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>

        {/* Score + Stats */}
        <div className="flex items-center gap-6">
          {/* Circular score gauge */}
          <div className="relative flex-shrink-0">
            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r="34"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="6"
              />
              <motion.circle
                cx="40"
                cy="40"
                r="34"
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 34}`}
                initial={{ strokeDashoffset: 2 * Math.PI * 34 }}
                animate={{
                  strokeDashoffset:
                    2 * Math.PI * 34 * (1 - result.score / 100),
                }}
                transition={{ duration: 1, ease: "easeOut" }}
                className={scoreColor.ring}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-bold text-slate-800">
                {animatedScore}
              </span>
            </div>
          </div>

          <div className="flex-1">
            <div
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-2 ${scoreColor.bg} ${scoreColor.ring.replace("text-", "text-")}`}
            >
              {scoreColor.label}
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-4">
              {result.stats.errorsCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-sm font-medium text-red-700">
                    {result.stats.errorsCount} error
                    {result.stats.errorsCount !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
              {result.stats.warningsCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-sm font-medium text-amber-700">
                    {result.stats.warningsCount} warning
                    {result.stats.warningsCount !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
              {result.stats.suggestionsCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-sm font-medium text-blue-700">
                    {result.stats.suggestionsCount} suggestion
                    {result.stats.suggestionsCount !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
              {result.stats.totalIssues === 0 && (
                <div className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-700">
                    No issues found
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Issues list */}
      {visibleIssues.length > 0 && (
        <div className="px-6 py-4 space-y-3 max-h-[480px] overflow-y-auto">
          <AnimatePresence mode="popLayout">
            {visibleIssues.map((issue, idx) => {
              const config = severityConfig[issue.severity];
              const SeverityIcon = config.icon;
              const isExpanded = expandedIssue === idx;

              return (
                <motion.div
                  key={`${issue.entryId}-${issue.type}-${idx}`}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20, scale: 0.95 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`rounded-xl border ${config.border} ${config.bg} p-4`}
                >
                  <div className="flex items-start gap-3">
                    {/* Severity icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      <SeverityIcon className={`w-4.5 h-4.5 ${config.iconColor}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}
                        >
                          {typeLabels[issue.type] || issue.type}
                        </span>
                      </div>

                      <p className={`text-sm ${config.text} mb-2`}>
                        {issue.message}
                      </p>

                      {/* Original + suggested fix (expandable) */}
                      {(issue.originalText || issue.suggestedFix) && (
                        <div>
                          <button
                            onClick={() =>
                              setExpandedIssue(isExpanded ? null : idx)
                            }
                            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors mb-1"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                            {isExpanded ? "Hide details" : "Show details"}
                          </button>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                {issue.originalText && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-slate-500 block mb-0.5">
                                      Original:
                                    </span>
                                    <p className="text-sm text-slate-600 bg-white/60 rounded-lg px-3 py-2 border border-slate-200/50">
                                      {issue.originalText}
                                    </p>
                                  </div>
                                )}
                                {issue.suggestedFix && (
                                  <div>
                                    <span className="text-xs font-medium text-emerald-600 block mb-0.5">
                                      Suggested:
                                    </span>
                                    <p className="text-sm text-emerald-700 bg-emerald-50/50 rounded-lg px-3 py-2 border border-emerald-200/50">
                                      {issue.suggestedFix}
                                    </p>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {issue.suggestedFix && (
                        <button
                          onClick={() => handleApplyFix(issue)}
                          disabled={applyingId === issue.entryId}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-[#0F5132] hover:bg-[#146C43] rounded-lg transition-colors disabled:opacity-50"
                        >
                          {applyingId === issue.entryId ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Check className="w-3 h-3" />
                          )}
                          Apply Fix
                        </button>
                      )}
                      <button
                        onClick={() => handleDismiss(issue)}
                        className="p-1.5 rounded-lg hover:bg-white/60 transition-colors"
                        title="Dismiss"
                      >
                        <X className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* All clear state */}
      {visibleIssues.length === 0 && result.stats.totalIssues > 0 && (
        <div className="px-6 py-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3"
          >
            <Check className="w-6 h-6 text-emerald-600" />
          </motion.div>
          <p className="text-sm font-medium text-slate-700">
            All issues resolved
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {appliedIds.size} fixed, {dismissedIds.size} dismissed
          </p>
        </div>
      )}

      {/* Empty: no issues at all */}
      {result.stats.totalIssues === 0 && (
        <div className="px-6 py-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3"
          >
            <Check className="w-6 h-6 text-emerald-600" />
          </motion.div>
          <p className="text-sm font-medium text-emerald-700">
            All entries look great
          </p>
          <p className="text-xs text-slate-500 mt-1">{result.summary}</p>
        </div>
      )}

      {/* Bottom actions */}
      {visibleIssues.length > 0 && (
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleFixAll}
              disabled={fixingAll}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0F5132] hover:bg-[#146C43] rounded-xl transition-colors disabled:opacity-50"
            >
              {fixingAll ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Fix All Issues
            </button>
            <button
              onClick={handleDismissAll}
              className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              Dismiss All
            </button>
          </div>
          <p className="text-xs text-slate-400 max-w-xs text-right">
            {result.summary}
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default AiPreBillReview;
